import {
  darken,
  getContrastRatio,
  lighten,
  PaletteColor,
  ThemeOptions,
} from '@mui/material';
import { LinkProps } from '@mui/material/Link';
import { DataVisualizationColor } from '@mui/material/styles/createPalette';
// the below import is for https://mui.com/x/react-date-pickers/getting-started/#typescript
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { Inter, Playfair_Display } from 'next/font/google';
import { forwardRef } from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';

import { ultraLight } from '@/styles/themes/paletteGenerator';
import { ShadowVariant } from '@/types/mui';

import { COLORS } from '../tokens/colors';
import {
  FONT_SIZING,
  FONT_WEIGHTS,
  TITLE_SERIF_SIZING,
  TYPOGRAPHY_SIZING,
} from '../tokens/fonts';
import { zIndexes } from '../zIndexes';

export function getContrastTextForColor(baseColor: string): string {
  const contrastRatio = getContrastRatio(baseColor, COLORS.PRIMITIVES.WHITE);
  // 4.5 is the default threshold for WCAG AA, but we use a slightly lower threshold because anecdotally,
  // white is slightly preferred
  const contrastThreshold = 3.3;

  // If contrast with white is high, the base color is dark
  // So we want to return a lightened version for contrast
  if (contrastRatio >= contrastThreshold) {
    return COLORS.PRIMITIVES.WHITE;
  }

  // If contrast with white is low, the base color is light
  // So we want to return a darkened version for contrast
  return darken(baseColor, 0.8); // Significantly darkened
}

/** The WCAG AA contrast ratio for normal text. */
const MIN_TEXT_CONTRAST = 4.5;
// Pure black, not the `#111827` black token. White or pure black has at
// least 4.58:1 contrast on every opaque color. The black token does not.
const PURE_BLACK = '#000000';

/**
 * Returns white or a darkened shade of the background, whichever has the
 * higher contrast ratio against it. If neither reaches the AA ratio, returns
 * pure black. Use it for text on a background of any hue, such as a custom
 * color.
 */
export function getReadableTextColor(background: string): string {
  const dark = darken(background, 0.8);
  const white = COLORS.PRIMITIVES.WHITE;
  const best =
    getContrastRatio(background, white) >= getContrastRatio(background, dark)
      ? white
      : dark;
  return getContrastRatio(background, best) >= MIN_TEXT_CONTRAST
    ? best
    : PURE_BLACK;
}

export const generatePaletteFromBaseColor = (
  baseColor: string
): PaletteColor => {
  return {
    main: baseColor,
    light: lighten(baseColor, 0.95),
    dark: darken(baseColor, 0.1),
    contrastText: getContrastTextForColor(baseColor),
  };
};

export const generateDataVisualizationPaletteFromBaseColor = (
  baseColor: string
): DataVisualizationColor => {
  return {
    main: baseColor,
    light: lighten(baseColor, 0.2),
    dark: darken(baseColor, 0.6),
    contrastText: getContrastTextForColor(baseColor),
    lighter: lighten(baseColor, 0.6),
    ultraLight: ultraLight(baseColor),
  };
};

// https://mui.com/material-ui/integrations/routing/#global-theme-link
const LinkBehavior = forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & {
    href: RouterLinkProps['to'];
    external?: RouterLinkProps['reloadDocument'];
    target?: RouterLinkProps['target'];
  }
>(function LinkBehavior(props, ref) {
  const { href, external, ...other } = props;
  // Map href (Material UI) -> to (react-router)
  return (
    <RouterLink ref={ref} to={href} reloadDocument={external} {...other} />
  );
});

export const SPACING_CONSTANT = 8;
const INPUT_PADDING = '10px 14px';

export const THEME_SHADOWS: Record<ShadowVariant, string> = {
  xs: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  sm: '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
  xsAmbient:
    '0 -2px 12px -10px rgba(17, 24, 39, 0.08), 0 2px 8px -4px rgba(17, 24, 39, 0.08)',
  smAmbient:
    '0 -2px 12px -6px rgba(17, 24, 39, 0.10), 0 2px 10px -3px rgba(17, 24, 39, 0.08)',
  md: '0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.10)',
  lg: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
  xl: '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  xxl: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
  xxxl: '0px 32px 64px -12px rgba(16, 24, 40, 0.14)',

  innerShadowLeftward:
    '-2px 0px 4px -2px rgba(16, 24, 40, 0.06) inset, -4px 0px 8px -2px rgba(16, 24, 40, 0.10) inset',
  innerShadowRightward:
    '4px 0px 8px -2px rgba(16, 24, 40, 0.10) inset, 2px 0px 4px -2px rgba(16, 24, 40, 0.06) inset',
  // same as md but with the shadow inset
  mdInset:
    'inset 0px 2px 4px -2px rgba(16, 24, 40, 0.06), inset 0px 4px 8px -2px rgba(16, 24, 40, 0.10)',
  // same as mdInset but with the shadow inverted as well
  mdInsetInvert:
    '0px -2px 4px -2px rgba(16, 24, 40, 0.06) inset, 0px -4px 8px -2px rgba(16, 24, 40, 0.10) inset',
  // same as lg but with the shadow cast upwards
  lgYInvert:
    '0px -4px 6px -2px rgba(16, 24, 40, 0.03), 0px -12px 16px -4px rgba(16, 24, 40, 0.08)',

  // same as md but with the shadow cast upwards
  mdYInvert:
    '0px -2px 4px -2px rgba(16, 24, 40, 0.06), 0px -4px 8px -2px rgba(16, 24, 40, 0.10)',
  rightInvert:
    '4px 0px 8px -2px rgba(16, 24, 40, 0.1), 2px 0 4px -2px rgba(16, 24, 40, 0.06)',
  rightInvertInset:
    'inset 4px 0px 8px -2px rgba(16, 24, 40, 0.1), inset 2px 0 4px -2px rgba(16, 24, 40, 0.06)',
  drawerShadowInset:
    'inset 0 20px 24px -4px rgba(16, 24, 40, 0.08), inset 0 8px 8px -4px rgba(16, 24, 40, 0.03)',
};

// Exported so the print layout can apply their `.variable` classNames and
// force next/font to preload them on print routes (see PrintOnlyLayout) —
// otherwise a font used only via its `fontFamily` string (e.g. Romie on
// headers) can miss the headless PDF capture and fall back to serif.
export const inter = Inter({
  subsets: ['latin'],
  variable: '--font-inter',
});

export const playfair = Playfair_Display({
  subsets: ['latin'],
  variable: '--font-playfair',
});

export const COMMON_THEME_PROPERTIES: ThemeOptions = {
  spacing: SPACING_CONSTANT,
  typography: {
    fontFamily: inter.style.fontFamily,
    fontWeightLight: FONT_WEIGHTS.light,
    fontWeightRegular: FONT_WEIGHTS.regular,
    fontWeightMedium: FONT_WEIGHTS.medium,
    fontWeightBold: FONT_WEIGHTS.bold,
    h1_display: {
      ...FONT_SIZING.xl,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
      fontFamily: playfair.style.fontFamily,
    },
    h2_display: {
      ...FONT_SIZING.lg,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
      fontFamily: playfair.style.fontFamily,
    },
    // The Figma "Title/Serif/m" style. It uses the same font as the display
    // variants. Branded themes change the font to the presentation header
    // face.
    // TODO(T2-11975): Make sure that all themes load the new serif and strong
    // variants correctly.
    // https://linear.app/luminary/issue/T2-11975/track-typography-style-overrides-and-new-serif-and-strong-variants
    'title-serif-m': {
      ...TITLE_SERIF_SIZING.m,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.PRIMARY,
      fontFamily: playfair.style.fontFamily,
    },
    h2light: {
      ...FONT_SIZING.xl,
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.light,
      color: COLORS.TEXT.PRIMARY,
    },
    'title-xl': {
      ...TYPOGRAPHY_SIZING['title-xl'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    'title-l': {
      ...TYPOGRAPHY_SIZING['title-l'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    'title-m': {
      ...TYPOGRAPHY_SIZING['title-m'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    'title-s': {
      ...TYPOGRAPHY_SIZING['title-s'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    'title-xs': {
      ...TYPOGRAPHY_SIZING['title-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    'body-m': {
      ...TYPOGRAPHY_SIZING['body-m'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.PRIMARY,
    },
    'body-s': {
      ...TYPOGRAPHY_SIZING['body-s'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.PRIMARY,
    },
    'body-xs': {
      ...TYPOGRAPHY_SIZING['body-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.SECONDARY,
    },
    'eyebrow-xs': {
      ...TYPOGRAPHY_SIZING['eyebrow-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.SECONDARY,
      textTransform: 'uppercase',
      letterSpacing: '0.0625rem', // 1px
    },
    h1: {
      ...TYPOGRAPHY_SIZING['title-xl'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    h2: {
      ...TYPOGRAPHY_SIZING['title-xl'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    h3: {
      ...TYPOGRAPHY_SIZING['title-l'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    h4: {
      ...TYPOGRAPHY_SIZING['title-l'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    h5: {
      ...TYPOGRAPHY_SIZING['title-s'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    h6: {
      ...TYPOGRAPHY_SIZING['title-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.bold,
      color: COLORS.TEXT.PRIMARY,
    },
    subtitle1: {
      ...TYPOGRAPHY_SIZING['body-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.SECONDARY,
    },
    subtitle2: {
      ...TYPOGRAPHY_SIZING['body-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.SECONDARY,
    },
    body1: {
      ...TYPOGRAPHY_SIZING['body-s'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.PRIMARY,
    },
    body2: {
      ...TYPOGRAPHY_SIZING['body-s'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      color: COLORS.TEXT.PRIMARY,
    },
    button: {
      // the default MUI style has this set to "uppercase"; reset that here.
      textTransform: 'none',
      ...TYPOGRAPHY_SIZING['body-xs'],
      fontFamily: inter.style.fontFamily,
      fontWeight: FONT_WEIGHTS.medium,
      // the color will be variable based on the treatment of the button,
      // e.g. primary vs secondary
    },
  },
  shape: {
    borderRadius: 4,
  },
  // if these aren't all set to `none`, MUI will pull the default shadow styles
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
  palette: {
    contrastThreshold: 2.5,
    common: {
      black: COLORS.PRIMITIVES.BLACK,
      white: COLORS.PRIMITIVES.WHITE,
    },
    divider: COLORS.GRAY[200],
    links: generatePaletteFromBaseColor(COLORS.NAVY[500]),
    buttons: generatePaletteFromBaseColor(COLORS.NAVY[500]),
    dataVisualizationPrimary: generateDataVisualizationPaletteFromBaseColor(
      COLORS.NAVY[300]
    ),
    dataVisualizationSecondary: generateDataVisualizationPaletteFromBaseColor(
      COLORS.TEAL[300]
    ),
    dataVisualizationTertiary: generateDataVisualizationPaletteFromBaseColor(
      COLORS.BLUE[300]
    ),
    dataVisualizationNegative: generateDataVisualizationPaletteFromBaseColor(
      COLORS.ORANGE[200]
    ),
    // https://www.figma.com/file/FlleF4k1X9St9vv3EgiSGY/Design-System?node-id=21%3A487&mode=dev
    shadows: THEME_SHADOWS,
    education: generatePaletteFromBaseColor(COLORS.FUNCTIONAL.WARNING[400]),
    background: {
      default: COLORS.GRAY[50],
      paper: COLORS.PRIMITIVES.WHITE,
    },
  },
  // TODO: figure out why this was breaking modals
  // breakpoints: {
  //   keys: ['sm', 'md', 'lg', 'xl'],
  //   values: {
  //     sm: 0,
  //     md: 1024,
  //     lg: 1280,
  //     xl: 1440,
  //   },
  // },
  zIndex: {
    drawer: zIndexes.CONTEXTUAL_HELP,
    modal: zIndexes.MODAL,
  },
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked, &.MuiCheckbox-indeterminate': {
            color: COLORS.GRAY[500],
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: COLORS.GRAY[500],
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': {
            backgroundColor: COLORS.GRAY[500],
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: ({ theme }) => ({
          padding: INPUT_PADDING,
          ...theme.typography['body-xs'],
          color: COLORS.TEXT.PRIMARY, // Input text uses the primary text color.
          // Use the semantic placeholder color instead of MUI's default
          // translucent primary text, so placeholders read as an empty field
          // and match the app's other placeholder/hint text.
          '&::placeholder': {
            color: COLORS.TEXT.PLACEHOLDER,
            opacity: 1,
          },
        }),
        root: {
          margin: 0,
        },
      },
      defaultProps: {},
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          padding: INPUT_PADDING,
          // Restore the InputAdornment height that MUI v4 and v5 set by
          // default (`height: '0.01em'`). MUI v6 removed it with IE 11 support.
          //
          // A start InputAdornment contains a hidden zero-width text span.
          // That span uses the body line height (about 20px), which is taller
          // than this input's text line (about 17px). Without this rule, a
          // wrapped icon makes the field about 3px taller than other inputs.
          // A near-zero box height stops the adornment from setting the row
          // height. The root centers its items, so the icon still renders
          // centered outside the collapsed box.
          //
          // Remove this rule if MUI stops rendering the hidden span, or if
          // the adornment text style matches the input text style.
          '& .MuiInputAdornment-positionStart': {
            height: '0.01em',
          },
        },
        input: {
          // since we want the padding/height of this component to be consistent
          // with the rest of the inputs, we're setting that on inputRoot and setting
          // it to 0 here; there were paddings in both places with the default implementation.
          paddingTop: '0px !important',
          paddingBottom: '0px !important',
          marginTop: '0px !important',
        },
        popper: ({ theme }) => ({
          marginTop: `${theme.spacing(0.5)} !important`,
        }),
        paper: ({ theme }) => ({
          borderRadius: theme.spacing(0.75),
          boxShadow: theme.palette.shadows.xs,
          border:
            theme.palette.mode === 'dark'
              ? 'none'
              : `1px solid ${COLORS.GRAY[200]}`,
        }),
        listbox: ({ theme }) => ({
          padding: theme.spacing(0.5),
          '& .MuiAutocomplete-option': {
            ...theme.typography['body-xs'],
            minHeight: theme.spacing(4.5),
            padding: theme.spacing(1.25, 1.5),
          },
        }),
        option: ({ theme }) => ({
          borderRadius: theme.spacing(0.5),
          // MUI's Autocomplete state selectors have higher specificity than
          // theme slot overrides. Selection keeps the default surface; hover
          // and keyboard focus provide the shared interaction background.
          '&[aria-selected="true"]': {
            backgroundColor: 'transparent !important',
          },
          '&:hover, &.Mui-focused, &.Mui-focusVisible, &[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focused':
            {
              backgroundColor: `${COLORS.FUNCTIONAL.HOVER} !important`,
            },
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          cursor: 'pointer',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: 0,
          marginTop: theme.spacing(0.5),
        }),
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true, // No more ripple, on the whole application 💣!
        LinkComponent: LinkBehavior,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${COLORS.GRAY[200]}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          borderRadius: theme.spacing(0.75),
          boxShadow: theme.palette.shadows.xs,
          border:
            theme.palette.mode === 'dark'
              ? 'none'
              : `1px solid ${COLORS.GRAY[200]}`,
        }),
        list: ({ theme }) => ({
          padding: theme.spacing(0.5, 0),
          '& .MuiMenuItem-root': {
            marginInline: theme.spacing(0.5),
          },
          '& > .MuiDivider-root, & > * > .MuiDivider-root': {
            border: 0,
            height: theme.spacing(1),
            marginBlock: 0,
            marginInline: 0,
            position: 'relative',
            '&::after': {
              borderTop: `1px solid ${COLORS.GRAY[200]}`,
              content: '""',
              left: 0,
              position: 'absolute',
              right: 0,
              top: '50%',
            },
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['body-xs'],
          borderRadius: theme.spacing(0.5),
          color: COLORS.TEXT.PRIMARY, // Override the text-secondary gray color with the primary text color
          ':hover': {
            backgroundColor: COLORS.FUNCTIONAL.HOVER,
          },
          // MenuItem marks keyboard focus with `Mui-focusVisible`, and MUI's own
          // selected + focus-visible rule is a three-class selector, so it has to be
          // matched at equal specificity to be overridden.
          '&.Mui-selected': {
            backgroundColor: 'transparent',
          },
          '&.Mui-focusVisible, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible':
            {
              backgroundColor: COLORS.FUNCTIONAL.HOVER,
            },
          minHeight: theme.spacing(4.5),
          paddingTop: theme.spacing(1.25),
          paddingBottom: theme.spacing(1.25),
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
        }),
        divider: {
          border: 0,
          marginBottom: SPACING_CONSTANT,
          overflow: 'visible',
          position: 'relative',
          '&::after': {
            borderTop: `1px solid ${COLORS.GRAY[200]}`,
            bottom: SPACING_CONSTANT * -0.5,
            content: '""',
            left: SPACING_CONSTANT * -0.5,
            position: 'absolute',
            right: SPACING_CONSTANT * -0.5,
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => ({
          boxShadow: theme.palette.shadows.lg,
          border:
            theme.palette.mode === 'dark'
              ? 'none'
              : `1px solid ${COLORS.GRAY[200]}`,
          marginTop: theme.spacing(0.5),
        }),
      },
    },
    MuiCalendarOrClockPicker: {
      styleOverrides: {
        root: ({ theme }) => ({
          boxShadow: theme.palette.shadows.lg,
          border:
            theme.palette.mode === 'dark'
              ? 'none'
              : `1px solid ${COLORS.GRAY[200]}`,
          marginTop: theme.spacing(0.5),
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          '&.MuiDivider-light': {
            borderColor: COLORS.PRIMITIVES.WHITE,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiPaper-root': {
            padding: theme.spacing(2),
            position: 'static', // Override MUI Paper's default position: relative
          },
        }),
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: COLORS.GRAY[300],
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },
    MuiListItemText: {
      defaultProps: {
        primaryTypographyProps: {
          variant: 'body-xs',
          style: { whiteSpace: 'normal', color: COLORS.TEXT.PRIMARY },
        }, // ListItemText should wrap text by default, you can override this with an ellipsis if you need to
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '& .MuiListItemIcon-root': {
            // Without setting the min-width here, the icon of the selected option
            // has a default width of 56px in the select input, which causes awkward
            // spacing between the icon and the text. 36px is the min-width of
            // the icon in the dropdown (as set by MUI).
            minWidth: 24,
          },
          // When a startAdornment icon is present on the parent input,
          // remove the select value's own left padding so the gap between
          // the icon and the text is only the adornment's 8px margin.
          '.MuiInputBase-adornedStart &': {
            paddingLeft: 0,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // Map title variants to heading elements for semantic HTML and accessibility
          'title-xl': 'h2',
          'title-l': 'h3',
          'title-m': 'h4',
          'title-s': 'h5',
          'title-xs': 'h6',
          // Keep existing mappings for legacy variants
          h1_display: 'h1',
          h2_display: 'h2',
          'title-serif-m': 'h4',
          h2light: 'h2',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
          '&:last-child': {
            paddingBottom: theme.spacing(2),
          },
        }),
      },
    },
  },
};
